import type { TokenPair } from '../api/types';
import { http } from '../http/client';
import { tokenStorage } from './tokenStorage';

/**
 * Duplicated tabs.
 *
 * "Duplicate tab" (and some "open in new tab" paths) copies the tab's sessionStorage, and with it
 * the refresh token. Refresh tokens rotate and a reused one revokes its whole family, so as soon as
 * both tabs refresh, both are signed out and the next load falls through to the silent SSO check.
 *
 * A tab therefore marks itself as open while it lives (the mark is removed on `pagehide`, so a
 * reload does not see it). A tab that boots and finds the mark already set may be a copy: it asks
 * the tab that owns the copied session, over a BroadcastChannel, for a one-time launch code of the
 * same organization and product, and exchanges it for a session of its own. When no tab answers,
 * the owner is gone and the copied session is this tab's alone.
 */
const TAG_KEY = 'architech.platform.v1.tabTag';
const OPEN_KEY = 'architech.platform.v1.tabOpen';
const CHANNEL = 'architech.platform.v1.tabs';
const ACK_TIMEOUT_MS = 500;
const CODE_TIMEOUT_MS = 15_000;

type TabMessage =
  | { type: 'fork'; tag: string; nonce: string }
  | { type: 'ack'; nonce: string }
  | { type: 'code'; nonce: string; code: string | null };

let channel: BroadcastChannel | null = null;
let watching = false;

function read(key: string): string | null {
  try { return window.sessionStorage.getItem(key); } catch { return null; }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch { /* storage blocked */ }
}

function getChannel(): BroadcastChannel | null {
  if (!channel && typeof BroadcastChannel !== 'undefined') channel = new BroadcastChannel(CHANNEL);
  return channel;
}

function tabTag(): string {
  let tag = read(TAG_KEY);
  if (!tag) { tag = crypto.randomUUID(); write(TAG_KEY, tag); }
  return tag;
}

/** Waits for the first message matching `accept`, or null after `ms`. */
function waitFor<T extends TabMessage>(bus: BroadcastChannel, accept: (m: TabMessage) => m is T, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const done = (value: T | null) => { window.clearTimeout(timer); bus.removeEventListener('message', onMessage); resolve(value); };
    const onMessage = (event: MessageEvent<TabMessage>) => { if (accept(event.data)) done(event.data as T); };
    const timer = window.setTimeout(() => done(null), ms);
    bus.addEventListener('message', onMessage);
  });
}

/**
 * Run before the copied refresh token is used. 'original' means this tab owns its session (or has
 * none); 'forked' means it now has a session of its own; 'failed' means the copy was dropped and the
 * tab continues as one without a session.
 */
export async function forkIfDuplicate(): Promise<'original' | 'forked' | 'failed'> {
  const bus = getChannel();
  const tag = read(TAG_KEY);
  if (!read(OPEN_KEY) || !tag || !bus || !tokenStorage.getRefreshContext()) return 'original';
  // From here on this tab is someone else's copy until proven otherwise: never answer for the owner.
  write(TAG_KEY, crypto.randomUUID());
  const nonce = crypto.randomUUID();
  const ack = waitFor(bus, (m): m is Extract<TabMessage, { type: 'ack' }> => m.type === 'ack' && m.nonce === nonce, ACK_TIMEOUT_MS);
  const answer = waitFor(bus, (m): m is Extract<TabMessage, { type: 'code' }> => m.type === 'code' && m.nonce === nonce, CODE_TIMEOUT_MS);
  bus.postMessage({ type: 'fork', tag, nonce } satisfies TabMessage);
  if (!(await ack)) return 'original';
  const reply = await answer;
  try {
    if (!reply?.code) throw new Error('The owning tab could not hand over a session.');
    const productCode = tokenStorage.getRefreshContext()?.productCode ?? 'PLATFORM';
    const { data } = await http().post<TokenPair>('/auth/product-launch/exchange',
      { code: reply.code, productCode }, { skipErrorToast: true, skipAuthRefresh: true });
    const claims = JSON.parse(atob(data.accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as { tid?: string };
    if (!claims.tid) throw new Error('The forked session names no organization.');
    tokenStorage.applyTokenPair(data, { organizationId: claims.tid, productCode });
    return 'forked';
  } catch {
    // Never touch the owner's refresh token: forget the copy (this tab's storage only).
    tokenStorage.clear();
    return 'failed';
  }
}

/** Marks this tab as open and answers fork requests for its session. Safe to call more than once. */
export function watchTabSession(): void {
  tabTag();
  write(OPEN_KEY, '1');
  if (watching) return;
  watching = true;
  window.addEventListener('pagehide', () => write(OPEN_KEY, null));
  window.addEventListener('pageshow', () => write(OPEN_KEY, '1'));
  getChannel()?.addEventListener('message', (event: MessageEvent<TabMessage>) => {
    const message = event.data;
    if (message?.type !== 'fork' || message.tag !== tabTag()) return;
    const bus = getChannel()!;
    bus.postMessage({ type: 'ack', nonce: message.nonce } satisfies TabMessage);
    const context = tokenStorage.getRefreshContext();
    const handOver = context
      ? http().post<{ code: string }>('/auth/product-launch', { productCode: context.productCode }, { skipErrorToast: true })
        .then(({ data }) => data.code, () => null)
      : Promise.resolve(null);
    void handOver.then((code) => bus.postMessage({ type: 'code', nonce: message.nonce, code } satisfies TabMessage));
  });
}
